import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import privateAxios from '../../utils/axios/privateAxios';
import publicAxios from '../../utils/axios/publicAxios';
import './CreatePost.css';
import Notification from '../../components/Notification/Notification';
import { Link, useNavigate } from 'react-router-dom';
import { formats, modules, postCategories } from '../../assets/exports';
import { jwtDecode } from 'jwt-decode';


const CreatePost = () => {
  const navigate = useNavigate();

  const [decodedData, setDecodedData] = useState({});
  const userRoles = decodedData?.userRoles || [];
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [display, setDisplay] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState(userRoles[0] || '');
  const [notification, setNotification] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification({ type: '', message: '' });
    }, 5000);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) return '';

    try {
      const response = await privateAxios.get('/private/generate-upload-url', {
        params: {
          filename: imageFile.name,
          filetype: imageFile.type,
        },
      });

      const { url } = response.data;
      await publicAxios.put(url, imageFile);

      return url.split('?')[0];
    } catch (error) {
      console.error('Image upload failed:', error);
      throw new Error('Failed to upload image.');
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);
  //   setStatus('');

  //   try {
  //     let uploadedImageUrl = '';
  //     if (imageFile) {
  //       uploadedImageUrl = await uploadImageToS3();
  //     }

  //     const postData = {
  //       title,
  //       content,
  //       display,
  //       imageUrl: uploadedImageUrl || '',
  //       category, // 👈 Include category
  //     };

  //     await privateAxios.post(`/private/create-post`, postData);

  //     showNotification('success', 'Post created successfully!');
  //     setTitle('');
  //     setContent('');
  //     setDisplay(false);
  //     setImageFile(null);
  //     setPreviewUrl('');
  //     setCategory(userRoles[0] || '');

  //     setTimeout(() => navigate('/admin-posts'), 3000);
  //   } catch (error) {
  //     console.error(error);
  //     showNotification('error', error.message || 'Error creating post.');
  //     setStatus('Error creating post.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    // 1. Extract all images from Quill content
    const div = document.createElement("div");
    div.innerHTML = content;
    const images = Array.from(div.querySelectorAll("img"));

    for (let img of images) {
      if (img.src.startsWith("data:")) { // Base64 or Blob detected
        // 2. Convert base64 to Blob
        const file = dataURLToFile(img.src, `post-image-${Date.now()}.png`);

        // 3. Upload to S3
        const uploadUrl = await getUploadUrlFromS3(file.name, file.type);
        await publicAxios.put(uploadUrl, file);

        // 4. Replace base64 with S3 URL
        img.src = uploadUrl.split("?")[0];
      }
    }

    // Updated HTML content
    const updatedContent = div.innerHTML;

    // Save post
    await privateAxios.post(`/private/create-post`, {
      title,
      content: updatedContent,
      category,
      display,
      imageUrl: previewUrl || ""
    });

    showNotification('success', 'Post created successfully!');
    setTimeout(() => navigate('/admin-posts'), 2000);
  } catch (err) {
    console.error(err);
    showNotification('error', 'Failed to create post.');
  } finally {
    setLoading(false);
  }
};

// Helper to convert base64 → File
function dataURLToFile(dataURL, filename) {
  const arr = dataURL.split(','), mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

// Get AWS S3 Signed URL
const getUploadUrlFromS3 = async (filename, filetype) => {
  const response = await privateAxios.get('/private/generate-upload-url', {
    params: { filename, filetype }
  });
  return response.data.url;
};

  const availableCategories = userRoles.includes('admin') ? postCategories : userRoles;

  useEffect(() => {
    const token = localStorage.getItem('token');

    // Ensure token exists before decoding
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedData(decoded);
      } catch (err) {
        console.error('Invalid token:', err);
        // Handle invalid token if necessary (e.g., redirect to login)
        navigate('/login');
      }
    }
  }, [navigate]);

  return (
    <div className="create-post-container">
      <Notification
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ type: '', message: '' })}
      />

      <Link to={'/admin-posts'} className="create-post-back-to-posts">Back to Posts</Link>

      <h2>Create Post</h2>

      <form onSubmit={handleSubmit}>
        <label>Title:</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <label>Content:</label>
        <ReactQuill
          value={content}
          onChange={setContent}
          theme="snow"
          modules={modules}
          formats={formats}
        />

        <label>Category:</label>
        <select className='create-post-select-category' value={category} onChange={(e) => setCategory(e.target.value)} required>
          {availableCategories.map((role) => (
            <option key={role} value={role}>
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </option>
          ))}
        </select>

        {userRoles.includes('admin') && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={display}
              onChange={() => setDisplay(!display)}
            />
            Display
          </label>
        )}

        <label>Upload Image (optional):</label>
        <input type="file" accept="image/*" onChange={handleImageChange} />
        {previewUrl && (
          <div className="uploaded-image-preview">
            <img src={previewUrl} alt="Selected preview" />
          </div>
        )}

        <button type="submit" disabled={loading}>
          {loading ? 'Posting...' : 'Post'}
        </button>
      </form>

      {status && <p className="status-message">{status}</p>}
    </div>
  );
};

export default CreatePost;